import { useEffect, useRef, useState } from "react";
import { Send, Loader2, Megaphone, Paperclip, X, FileText } from "lucide-react";
import { canPreview, viewBlob } from "@/components/FileViewer";
import { toast } from "sonner";

const SCOPE_LABEL = {
  class: "Class announcement",
  school: "School announcement",
  district: "District announcement",
  bus: "Bus update",
};

const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/msg-files`;
const MAX_FILE_MB = 5;

export function readFileAsAttachment(file, onReady, onError) {
  if (file.size > MAX_FILE_MB * 1024 * 1024) {
    onError(`File too large — max ${MAX_FILE_MB} MB.`);
    return;
  }
  if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
    onError("Only images and PDF files can be attached.");
    return;
  }
  const reader = new FileReader();
  reader.onload = () => onReady({ name: file.name, data_url: reader.result });
  reader.onerror = () => onError("Could not read the file.");
  reader.readAsDataURL(file);
}

export function AttachmentView({ att, mine }) {
  if (!att) return null;
  const url = `${FILE_BASE}/${att.file_id}`;
  if (att.is_image) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-1.5" data-testid={`attachment-img-${att.file_id}`}>
        <img src={url} alt={att.name} className="max-h-48 rounded-lg border border-black/10 object-cover" loading="lazy" />
      </a>
    );
  }
  const previewable = /\.pdf$/i.test(att.name || "") || canPreview(att.name || "");
  const open = async (e) => {
    if (!previewable) return;
    e.preventDefault();
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error();
      viewBlob(await res.blob(), { title: att.name || "Attachment", filename: att.name || "" });
    } catch { toast.error("Could not open the attachment"); }
  };
  return (
    <a
      href={url} target="_blank" rel="noreferrer" onClick={open} data-testid={`attachment-file-${att.file_id}`}
      className={`mt-1.5 inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium ${
        mine ? "bg-white/70 text-sky-900 hover:bg-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      } transition`}
    >
      <FileText className="h-4 w-4" /> {att.name || "Attachment"}
    </a>
  );
}

export function MessageBubble({ m, mine }) {
  const broadcast = SCOPE_LABEL[m.scope];
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
        mine ? "bg-sky-100 border border-sky-200 text-slate-900 rounded-br-md" : "bg-white border border-slate-200 text-slate-900 rounded-bl-md"
      }`}>
        <div className={`text-[11px] font-semibold mb-0.5 ${mine ? "text-sky-800" : "text-slate-500"}`}>
          {m.sender_name}
          {broadcast && (
            <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
              mine ? "bg-white/70 text-sky-900" : "bg-amber-100 text-amber-800"
            }`}>
              <Megaphone className="h-2.5 w-2.5" /> {broadcast}
            </span>
          )}
        </div>
        {m.to_teacher_name && <div className={`text-[10px] font-semibold mb-0.5 ${mine ? "text-sky-800" : "text-primary"}`} data-testid={`msg-to-${m.id}`}>To: {m.to_teacher_name}</div>}
        {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
        <AttachmentView att={m.attachment} mine={mine} />
        <div className={`text-[10px] mt-1 ${mine ? "text-sky-700/70" : "text-slate-400"}`}>
          {new Date(m.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </div>
      </div>
    </div>
  );
}

export default function ChatThread({ messages, onSend, mySenderType, emptyText = "No messages yet." }) {
  const [draft, setDraft] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attError, setAttError] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setAttError("");
    readFileAsAttachment(f, setAttachment, setAttError);
  };

  const isTouch = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

  const autoGrow = (el) => {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
  };

  const submit = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if ((!body && !attachment) || sending) return;
    setSending(true);
    const ok = await onSend(body, attachment);
    setSending(false);
    if (ok) {
      setDraft("");
      setAttachment(null);
      if (taRef.current) taRef.current.style.height = "auto";
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto space-y-3 p-4 bg-slate-50" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-400 py-10">{emptyText}</div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} m={m} mine={m.sender_type === mySenderType} />
        ))}
        <div ref={endRef} />
      </div>
      {(attachment || attError) && (
        <div className="px-3 pt-2 bg-white border-t border-slate-200">
          {attachment && (
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium" data-testid="chat-attachment-chip">
              <Paperclip className="h-3 w-3" /> {attachment.name}
              <button type="button" onClick={() => setAttachment(null)} data-testid="chat-attachment-remove" className="hover:text-rose-600"><X className="h-3 w-3" /></button>
            </span>
          )}
          {attError && <span className="text-xs text-rose-600" data-testid="chat-attachment-error">{attError}</span>}
        </div>
      )}
      <form onSubmit={submit} className={`flex items-end gap-2 bg-white p-3 ${attachment || attError ? "" : "border-t border-slate-200"}`}>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={pickFile} data-testid="chat-file-input" />
        <button
          type="button" onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn"
          className="h-10 w-10 shrink-0 rounded-xl border border-slate-200 text-slate-500 inline-flex items-center justify-center hover:bg-slate-50 hover:text-slate-900 transition"
          title="Attach a photo or PDF"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); autoGrow(e.target); }}
          onKeyDown={(e) => {
            // Desktop: Enter sends, Shift+Enter = new line. Touch keyboards: Enter always makes a new line.
            if (e.key === "Enter" && !e.shiftKey && !isTouch) { e.preventDefault(); submit(e); }
          }}
          rows={1}
          placeholder="Write a message…"
          data-testid="chat-input"
          className="flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none max-h-32 overflow-y-auto"
        />
        <button
          type="submit"
          disabled={sending || (!draft.trim() && !attachment)}
          data-testid="chat-send-btn"
          className="h-10 w-10 shrink-0 rounded-xl bg-primary text-white inline-flex items-center justify-center hover:shadow disabled:opacity-50 transition"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
